import type { LaadMoment, AansluitingType } from './berekenAdvies';

export interface ZwareVerbruikers {
  warmtepomp: boolean;
  inductie: boolean;
  airco: boolean;
  boiler: boolean;
}

export interface StapLaadvoorkeurState {
  laadMoment: LaadMoment | null;
  beschikbareUren: number;
  laadtElders: 'nee' | 'soms' | 'regelmatig';
  zonnepanelen: boolean;
  aansluitingType: AansluitingType | null;
  zwareVerbruikers: ZwareVerbruikers;
}

interface Props {
  state: StapLaadvoorkeurState;
  onChange: (next: StapLaadvoorkeurState) => void;
  onNext: () => void;
  onBack: () => void;
  heeftEV: boolean | null;
}

const LAAD_MOMENTEN: { value: LaadMoment; label: string; omschrijving: string; defaultUren: number }[] = [
  {
    value: 'nacht',
    label: "'s Nachts",
    omschrijving: 'Auto staat 8+ uur',
    defaultUren: 8,
  },
  {
    value: 'overdag',
    label: 'Overdag',
    omschrijving: 'Auto staat wisselend',
    defaultUren: 5,
  },
];

const AANSLUITING_OPTIES: { value: AansluitingType; label: string; omschrijving: string }[] = [
  { value: '1fase', label: '1-fase', omschrijving: 'Standaard bij de meeste woningen' },
  { value: '3fase', label: '3-fase', omschrijving: 'Zwaarder, bijv. bij warmtepomp of inductie' },
  { value: 'weet_niet', label: 'Weet ik niet', omschrijving: '' },
];

const LAADT_ELDERS_OPTIES: { value: 'nee' | 'soms' | 'regelmatig'; label: string }[] = [
  { value: 'nee', label: 'Nee, alleen thuis' },
  { value: 'soms', label: 'Soms (werk/snellader)' },
  { value: 'regelmatig', label: 'Ja, regelmatig elders' },
];

function formatUren(uren: number): string {
  if (uren === 1) return '1 uur';
  if (Number.isInteger(uren)) return `${uren} uur`;
  const geheel = Math.floor(uren);
  return `${geheel}½ uur`;
}

export default function StapLaadvoorkeur({ state, onChange, onNext, onBack, heeftEV }: Props) {
  void heeftEV;
  const canProceed = state.laadMoment !== null;

  function handleLaadMomentChange(moment: LaadMoment) {
    const defaultUren = LAAD_MOMENTEN.find((m) => m.value === moment)?.defaultUren ?? 8;
    onChange({ ...state, laadMoment: moment, beschikbareUren: defaultUren });
  }

  return (
    <div className="lc-stap lc-stap--laadvoorkeur">
      <h2 className="lc-stap__titel">Stap 3 — Jouw laadvoorkeur</h2>

      {/* Laadmoment */}
      <fieldset className="lc-fieldset">
        <legend className="lc-label">Wanneer wil je het liefst laden?</legend>
        <div className="lc-radio-groep">
          {LAAD_MOMENTEN.map(({ value, label, omschrijving }) => (
            <label
              key={value}
              className={`lc-radio-kaart${state.laadMoment === value ? ' lc-radio-kaart--actief' : ''}`}
            >
              <input
                type="radio"
                name="laadmoment"
                value={value}
                checked={state.laadMoment === value}
                onChange={() => handleLaadMomentChange(value)}
                className="lc-radio-kaart__input"
              />
              <span className="lc-radio-kaart__label">{label}</span>
              <span className="lc-radio-kaart__omschrijving">{omschrijving}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Beschikbare uren slider */}
      <div className="lc-veld">
        <label className="lc-label" htmlFor="lc-beschikbare-uren">
          Hoeveel uur heb je beschikbaar om te laden?
          <span className="lc-slider__waarde"> {formatUren(state.beschikbareUren)}</span>
        </label>
        <p className="lc-info-tekst">Hoe langer de auto stil staat, hoe minder haast het laden heeft en hoe lager het benodigde laadvermogen.</p>
        <input
          id="lc-beschikbare-uren"
          type="range"
          className="lc-slider"
          min={1}
          max={12}
          step={0.5}
          value={state.beschikbareUren}
          onChange={(e) => onChange({ ...state, beschikbareUren: Number(e.target.value) })}
        />
        <div className="lc-slider__labels">
          <span>1 uur</span>
          <span>12 uur</span>
        </div>
      </div>

      {/* Laadt elders */}
      <fieldset className="lc-fieldset">
        <legend className="lc-label">Verwacht je ook buiten de deur te laden?</legend>
        <p className="lc-info-tekst">Als je verwacht ook bij het werk of onderweg te laden, heb je thuis minder laadvermogen nodig.</p>
        <div className="lc-toggle-groep lc-toggle-groep--column">
          {LAADT_ELDERS_OPTIES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`lc-toggle${state.laadtElders === value ? ' lc-toggle--actief' : ''}`}
              onClick={() => onChange({ ...state, laadtElders: value })}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      {/* Zonnepanelen */}
      <fieldset className="lc-fieldset">
        <legend className="lc-label">Heb je zonnepanelen, of ben je van plan die te nemen?</legend>
        <div className="lc-toggle-groep">
          <button
            type="button"
            className={`lc-toggle${state.zonnepanelen ? ' lc-toggle--actief' : ''}`}
            onClick={() => onChange({ ...state, zonnepanelen: true })}
          >
            Ja
          </button>
          <button
            type="button"
            className={`lc-toggle${!state.zonnepanelen ? ' lc-toggle--actief' : ''}`}
            onClick={() => onChange({ ...state, zonnepanelen: false })}
          >
            Nee
          </button>
        </div>
        {state.zonnepanelen && (
          <p className="lc-info-tekst">
            Met zonnepanelen kun je overdag gratis laden. Een slimme laadpaal kan hier automatisch op inspelen.
          </p>
        )}
      </fieldset>

      {/* Zware verbruikers */}
      <fieldset className="lc-fieldset">
        <legend className="lc-label">Welke van deze apparaten heb je thuis (of verwacht je te krijgen)?</legend>
        <p className="lc-info-tekst">Deze apparaten trekken veel stroom en kunnen invloed hebben op hoeveel laadvermogen je meterkast aankan.</p>
        <div className="lc-checkbox-groep">
          {(
            [
              { key: 'warmtepomp', label: 'Warmtepomp' },
              { key: 'inductie', label: 'Inductiekookplaat' },
              { key: 'airco', label: 'Airconditioning' },
              { key: 'boiler', label: 'Boiler' },
            ] as { key: keyof ZwareVerbruikers; label: string }[]
          ).map(({ key, label }) => (
            <label key={key} className="lc-checkbox-kaart">
              <input
                type="checkbox"
                className="lc-checkbox-kaart__input"
                checked={state.zwareVerbruikers[key]}
                onChange={(e) =>
                  onChange({
                    ...state,
                    zwareVerbruikers: { ...state.zwareVerbruikers, [key]: e.target.checked },
                  })
                }
              />
              <span className="lc-checkbox-kaart__label">{label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Meterkast aansluiting */}
      <fieldset className="lc-fieldset">
        <legend className="lc-label">Weet je misschien welke stroomaansluiting je meterkast nu heeft?</legend>
        <p className="lc-info-tekst">Dit helpt ons inschatten welk laadvermogen haalbaar is voor jouw situatie. Weet je het niet? Geen probleem, kies dan "Weet ik niet".</p>
        <div className="lc-toggle-groep lc-toggle-groep--aansluiting">
          {AANSLUITING_OPTIES.map(({ value, label, omschrijving }) => (
            <button
              key={value}
              type="button"
              className={`lc-toggle lc-toggle--aansluiting${state.aansluitingType === value ? ' lc-toggle--actief' : ''}`}
              onClick={() => onChange({ ...state, aansluitingType: value })}
            >
              <span className="lc-toggle__label">{label}</span>
              {omschrijving && <span className="lc-toggle__omschrijving">{omschrijving}</span>}
            </button>
          ))}
        </div>
        {state.aansluitingType === 'weet_niet' && (
          <p className="lc-info-tekst lc-info-tekst--uitgelicht">
            Dat is precies wat ons meterkastrapport voor je uitzoekt.
          </p>
        )}
      </fieldset>

      {/* Navigation */}
      <div className="lc-navigatie">
        <button type="button" className="lc-btn lc-btn--secundair" onClick={onBack}>
          ← Terug
        </button>
        <button
          type="button"
          className="lc-btn lc-btn--primair"
          disabled={!canProceed}
          onClick={onNext}
        >
          Bereken mijn advies →
        </button>
      </div>
    </div>
  );
}
